import { useCallback, useRef } from "react";

/**
 * Prefetch on user intent (hover/focus/touch) — fires once per element.
 * Pass a loader that imports the route chunk and/or warms data.
 *
 * Usage:
 *   const bind = usePrefetchOnIntent(() => import("@/pages/Episode"));
 *   <Link {...bind} to="/episode/123">…</Link>
 */
export function usePrefetchOnIntent(loader: () => Promise<unknown> | void) {
  const firedRef = useRef(false);

  const trigger = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    try {
      const r = loader();
      if (r && typeof (r as Promise<unknown>).catch === "function") {
        (r as Promise<unknown>).catch(() => {
          firedRef.current = false;
        });
      }
    } catch {
      firedRef.current = false;
    }
  }, [loader]);

  return {
    onMouseEnter: trigger,
    onFocus: trigger,
    onTouchStart: trigger,
  };
}
